import type {
    CareJourneyChecklistItem,
    CareJourneyNextStepsResponse,
} from '@/lib/store/api/careJourneyApi'

export type CareJourneyStepType = 'Lab' | 'Questionnaire' | 'Visit'

export function getCareJourneyChecklist(data: CareJourneyNextStepsResponse | undefined): CareJourneyChecklistItem[] {
    if (!data) return []
    if (data.checklist?.length) return data.checklist
    return data.groups?.flatMap((group) => group.criteria) ?? []
}

export function isCareJourneyChecklistItemActionable(item: CareJourneyChecklistItem): boolean {
    return item.status !== 'completed' && item.status !== 'locked'
}

export function isCareJourneyCheckInActionable(data: CareJourneyNextStepsResponse | undefined): boolean {
    return (data?.summary?.actionable_count ?? 0) > 0
}

export function resolveCareJourneyStepType(item: CareJourneyChecklistItem): CareJourneyStepType | null {
    if (item.type === 'Lab' || item.type === 'Questionnaire' || item.type === 'Visit') {
        return item.type
    }
    if (item.required_type === 'Questionnaire') return 'Questionnaire'
    if (item.sku_requirement_kind === 'lab') return 'Lab'
    if (item.sku_requirement_kind === 'visit') return 'Visit'
    return null
}

export function getActionableCareJourneyStepTypes(data: CareJourneyNextStepsResponse | undefined): CareJourneyStepType[] {
    const seenTypes = new Set<CareJourneyStepType>()
    for (const item of getCareJourneyChecklist(data)) {
        if (!isCareJourneyChecklistItemActionable(item)) continue
        const stepType = resolveCareJourneyStepType(item)
        if (stepType) seenTypes.add(stepType)
    }
    return Array.from(seenTypes)
}

/**
 * Distinct medication display names across actionable check-in items, in
 * first-seen order. Falls back to formulation names when concise labels are absent.
 */
export function getActionableCareJourneyMedicationNames(data: CareJourneyNextStepsResponse | undefined): string[] {
    const seenLowercaseKeys = new Set<string>()
    return getCareJourneyChecklist(data)
        .filter(isCareJourneyChecklistItemActionable)
        .flatMap((item) =>
            item.medication_display_names?.length ? item.medication_display_names : (item.medication_names ?? []),
        )
        .map((name) => name.trim())
        .filter((name) => {
            const lowercaseKey = name.toLowerCase()
            if (!name || seenLowercaseKeys.has(lowercaseKey)) return false
            seenLowercaseKeys.add(lowercaseKey)
            return true
        })
}
